/** Pure numbers behind the uptime visuals: the ticking on-air clock, the runs timeline and the monthly capacity bar. */

const pad = (n: number) => String(n).padStart(2, "0");

/** A clock that ticks every second: 1_000 → "00:00:01", 13 days and change → "13d 04:17:09". */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86_400);
  const hms = `${pad(Math.floor(total / 3600) % 24)}:${pad(Math.floor(total / 60) % 60)}:${pad(total % 60)}`;
  return d ? `${d}d ${hms}` : hms;
}

export interface RunLike { id: string; startedAt: string; wentLiveAt: string | null; endedAt: string | null; exitReason: string | null }

export interface Segment {
  id: string;
  /** Fractions of the timeline, 0–1. `live` equals `start` when the run never went live. */
  start: number;
  live: number;
  end: number;
  running: boolean;
  wentLive: boolean;
  /** Ended by a fault (anything but a stop the user or the schedule asked for). */
  fault: boolean;
}

export interface Timeline { from: number; to: number; segments: Segment[]; liveMs: number; uptime: number }

const CLEAN_ENDS = new Set(["stopped_by_user", "scheduled_end", "completed"]);

/**
 * Lays runs out on one time axis, from the first run's start to `now`, and sums the time they were live.
 * `uptime` is live time over the whole span, so gaps between runs (stopped or retrying) count against it.
 */
export function runTimeline(runs: RunLike[], now: number): Timeline | null {
  const parsed = runs
    .map((r) => {
      const start = Date.parse(r.startedAt);
      const live = r.wentLiveAt ? Date.parse(r.wentLiveAt) : null;
      const end = r.endedAt ? Date.parse(r.endedAt) : now;
      return { r, start, live, end: Math.max(start, Math.min(end, now)) };
    })
    .filter((x) => Number.isFinite(x.start))
    .sort((a, b) => a.start - b.start);
  if (!parsed.length) return null;
  const from = parsed[0]!.start;
  const to = Math.max(now, from + 1);
  const span = to - from;
  const at = (t: number) => (t - from) / span;
  let liveMs = 0;
  const segments = parsed.map(({ r, start, live, end }) => {
    const liveAt = live === null || !Number.isFinite(live) ? end : Math.min(Math.max(live, start), end);
    liveMs += end - liveAt;
    return {
      id: r.id,
      start: at(start),
      live: live === null ? at(start) : at(liveAt),
      end: at(end),
      running: r.endedAt === null,
      wentLive: live !== null,
      fault: r.endedAt !== null && !CLEAN_ENDS.has(r.exitReason ?? ""),
    };
  });
  return { from, to, segments, liveMs, uptime: Math.min(1, liveMs / span) };
}

/**
 * Hours a workspace could have streamed this month so far: every allowed stream slot, live since `from`.
 * The "stream hours" bar shows used hours against this.
 */
export function capacityHours(from: string, now: number, slots: number): number {
  const t = Date.parse(from);
  if (!Number.isFinite(t) || slots <= 0) return 0;
  return (Math.max(0, now - t) / 3_600_000) * slots;
}
